import { Chip } from '@change/ds-core-ui/Chip';
import { Text } from '@change/ds-core-ui/Text';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { TopicsFilterSkeleton } from './components/TopicsFilterSkeleton';
import { useTopicsFilter } from './hooks/useTopicsFilter';

type TopicsFilterProps = {
	showTitle?: boolean;
	chipSize?: 'small' | 'large';
	trackingContext: 'landing_topic' | 'topic_filter';
};

export const TopicsFilter: React.FC<TopicsFilterProps> = ({ showTitle, chipSize, trackingContext }) => {
	return (
		<ClientRender placeholder={<TopicsFilterSkeleton showTitle={showTitle} chipSize={chipSize} />}>
			<TopicsFilterContent showTitle={showTitle} chipSize={chipSize} trackingContext={trackingContext} />
		</ClientRender>
	);
};

const TopicsFilterContent: React.FC<TopicsFilterProps> = ({ showTitle, chipSize, trackingContext }) => {
	const {
		data: { topics, disabled, isInitialLoading },
		actions: { setTopic, trackTopicsFilterClick },
	} = useTopicsFilter();

	if (isInitialLoading) {
		return <TopicsFilterSkeleton showTitle={showTitle} chipSize={chipSize} />;
	}

	if (topics.length === 0) {
		return null;
	}

	return (
		<div>
			{showTitle && (
				<Text as="h2" variant="heading" size="x-small" fontWeight="normal" className="mb-4">
					<Translate value="corgi.search.filters.topic.title" />
				</Text>
			)}
			<div className="flex w-full flex-wrap gap-2">
				{topics.map((topic) => {
					return (
						<Chip
							key={topic.value}
							as="button"
							active={topic.isRefined}
							// Selected chips stay clickable so they can be removed even when there are no results.
							disabled={disabled && !topic.isRefined}
							onClick={() => {
								setTopic(topic.value);
								trackTopicsFilterClick({
									topic_value: topic.value,
									context: trackingContext,
								});
							}}
							size={chipSize}
							data-qa="topic-filter-chip"
						>
							{topic.label}
						</Chip>
					);
				})}
			</div>
		</div>
	);
};
